<script setup>
import { computed, ref, watch, onMounted, defineAsyncComponent } from 'vue';
import { useCurrentTheme } from '@/Composables/useTheme';
import { Link } from '@inertiajs/vue3';
import axios from 'axios';

// Lazy — ApexCharts (~500 KB) is no longer registered globally in app.js. Loading
// it here as an async component keeps it out of the eager bundle so it downloads
// only when a price graph actually renders. In <script setup> a component-valued
// const is auto-available in the template, so <apexchart> keeps working.
const apexchart = defineAsyncComponent(() => import('vue3-apexcharts'));

const props = defineProps({
    productId: Number,
});

const isMobile = ref(false);

/*
 * Grafen tegnes i JavaScript og foelger derfor ikke CSS-temaet af sig selv.
 * Med mode: 'dark' og #adb5bd haardkodet stod akseetiketter,
 * signaturforklaring og gitter lysegraat paa hvidt i lyst tema.
 *
 * computed frem for ref: farverne skal skifte naar brugeren skifter tema,
 * uden at siden genindlaeses. Objektet muteres ikke andre steder.
 */
const { isLight } = useCurrentTheme();

const tekstfarve = computed(() => (isLight.value ? '#5F6675' : '#adb5bd'));

const chartOptions = computed(() => ({
    theme: {
        mode: isLight.value ? 'light' : 'dark',
    },
    chart: {
        type: 'line',
        zoom: { enabled: true },
        toolbar: { show: true },
        background: 'transparent',
        foreColor: tekstfarve.value,
        dropShadow: {
            enabled: true,
            top: 0,
            left: 0,
            blur: 3,
            opacity: 0.5
        }
    },
    dataLabels: {
        enabled: false,
    },
    stroke: { curve: 'stepline', width: 2 },
    title: {
        text: 'Prishistorik',
        align: 'left',
        style: { fontSize: '14px', color: '#e4e6ea' }
    },
    xaxis: {
        type: 'datetime',
        categories: [],
        labels: {
            rotate: -45,
            rotateAlways: false,
            formatter: function (value) {
                return new Intl.DateTimeFormat('da-DK', {
                    day: '2-digit',
                    month: 'short',
                }).format(new Date(value));
            },
            style: { fontSize: '10px', colors: ['#adb5bd'] }
        }
    },
    yaxis: {
        labels: {
            formatter: function (val) {
                return new Intl.NumberFormat('da-DK', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(val) + ' kr.';
            },
            style: { fontSize: '11px', colors: [tekstfarve.value] }
        }
    },
    grid: {
        borderColor: isLight.value ? 'rgba(10,12,16,.10)' : 'rgba(255,255,255,0.1)',
    },
    legend: {
        labels: { colors: [tekstfarve.value] }
    },
    /*
     * Tooltip'en fortæller hvad den lange streg betyder.
     *
     * Serien har kun et punkt hvor prisen ÆNDREDE sig, og stepline tegner den
     * vandrette linje imellem. ApexCharts snapper til nærmeste punkt, så pegede
     * man midt i en tre måneder lang flad strækning, fik man datoen for den
     * ændring der startede den — teknisk et svar, men ikke det man spurgte om.
     *
     * Nu står perioden: fra-til og hvor længe. Og butikken, som har ligget i
     * databasen på hvert eneste af de 3,2 mio. historikpunkter uden nogensinde
     * at blive vist.
     */
    tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            const punkt = w.config.series[seriesIndex].data[dataPointIndex];
            if (!punkt) return '';

            const naeste = w.config.series[seriesIndex].data[dataPointIndex + 1];
            const start = new Date(punkt.x);
            // Sidste punkt løber til i dag; de øvrige til næste ændring.
            const slut = naeste ? new Date(naeste.x) : new Date();

            const dato = (d) => new Intl.DateTimeFormat('da-DK', {
                day: 'numeric', month: 'short', year: 'numeric',
            }).format(d);

            const dage = Math.max(0, Math.round((slut - start) / 86400000));

            const pris = new Intl.NumberFormat('da-DK', {
                style: 'currency', currency: 'DKK', minimumFractionDigits: 2,
            }).format(punkt.y);

            // Én dag er ikke en periode, og "1. april – 1. april" ser forkert ud.
            const periode = dage >= 1
                ? `${dato(start)} – ${dato(slut)}`
                : dato(start);

            const varighed = dage >= 1
                ? `<div class="pg-tip-varighed">uændret i ${dage} ${dage === 1 ? 'dag' : 'dage'}</div>`
                : '';

            const butik = punkt.butik
                ? `<div class="pg-tip-butik">hos ${punkt.butik}</div>`
                : '';

            return `<div class="pg-tip">
                <div class="pg-tip-periode">${periode}</div>
                <div class="pg-tip-pris">${pris}</div>
                ${butik}
                ${varighed}
            </div>`;
        },
    },
    responsive: [{
        breakpoint: 576,
        options: {
            chart: { toolbar: { show: false } },
            title: { style: { fontSize: '12px' } },
            xaxis: { labels: { style: { fontSize: '8px' } } },
            yaxis: { labels: { style: { fontSize: '9px' } } },
        }
    }]
}));

const series = ref([
    {
        name: 'Pris',
        data: []
    }
]);

const chartHeight = ref('300');
// Render the chart only after mount so the async ApexCharts component never
// resolves during SSR (keeps product-page server rendering safe + fast).
const isMounted = ref(false);

// Anonymous visitors only receive the last few months of history; logged-in
// users get the full series. Default true so the hint never flashes before the
// data loads. Set from the endpoint's full_history / limited_to_months fields.
const fullHistory = ref(true);
const limitedMonths = ref(3);

onMounted(() => {
    isMounted.value = true;
    if (typeof window !== 'undefined') {
        chartHeight.value = window.innerWidth < 576 ? '220' : '300';
    }

    axios.get(`/product/${props.productId}/price-graph-data`).then(response => {
        /*
         * punkter er den nye form og bærer butikken med. categories/prices er
         * den gamle, og de bliver: under en udrulning kan klient og server
         * være ude af takt et øjeblik, og en tom graf er værre end en graf
         * uden butiksnavn.
         */
        const raa = Array.isArray(response.data.punkter) && response.data.punkter.length
            ? response.data.punkter.map((p) => ({ x: p.dato, y: parseFloat(p.pris), butik: p.butik || null }))
            : response.data.categories.map((date, i) => ({
                x: date, // string in ISO format, e.g. "2025-04-10"
                y: parseFloat(response.data.prices[i]), // convert "2579.00" to 2579
                butik: null,
            }));

        const formattedSeries = raa;

        series.value[0].data = formattedSeries;

        if (typeof response.data.full_history !== 'undefined') {
            fullHistory.value = response.data.full_history;
        }
        if (response.data.limited_to_months) {
            limitedMonths.value = response.data.limited_to_months;
        }

    }).catch(error => {
        console.error('Error fetching price graph data:', error);
    });
});
</script>

<template>
    <div class="price-graph-container">
        <apexchart
            v-if="isMounted"
            type="line"
            :options="chartOptions"
            :series="series"
            width="100%"
            :height="chartHeight">
        </apexchart>

        <p v-if="isMounted && !fullHistory" class="price-graph-login-hint">
            Viser de seneste {{ limitedMonths }} måneder.
            <Link :href="route('login')" class="price-graph-login-link">Log ind</Link>
            for at se den fulde prishistorik.
        </p>
    </div>
</template>

<style scoped>
.price-graph-container {
    border: 1px solid var(--pc-border, #dee2e6);
    border-radius: 0.5rem;
    background-color: var(--pc-bg-secondary, #f8f9fa);
    padding: 1rem;
    margin-top: 1rem;
    overflow: hidden;
}

.price-graph-login-hint {
    margin: 0.5rem 0 0;
    font-size: 0.85rem;
    color: var(--pc-text-muted, #6c757d);
    text-align: center;
}

.price-graph-login-link {
    font-weight: 600;
    text-decoration: underline;
}

@media (max-width: 575.98px) {
    .price-graph-container {
        padding: 0.5rem;
        margin-left: -0.5rem;
        margin-right: -0.5rem;
        border-radius: 0;
        border-left: none;
        border-right: none;
    }
}
</style>

<!--
    Tooltip'en renderes af ApexCharts UDEN FOR komponentens træ, så scoped
    styling rammer den ikke. Derfor en global blok — afgrænset med pg-tip, som
    kun bruges her.
-->
<style>
.pg-tip {
    padding: 0.55rem 0.7rem;
    font-size: 0.8rem;
    line-height: 1.35;
}

.pg-tip-periode {
    color: #adb5bd;
    font-size: 0.72rem;
    margin-bottom: 0.15rem;
}

.pg-tip-pris {
    font-weight: 700;
    font-size: 0.95rem;
    color: #e4e6ea;
}

.pg-tip-butik {
    color: #e4e6ea;
}

.pg-tip-varighed {
    color: #adb5bd;
    font-size: 0.72rem;
    margin-top: 0.15rem;
}
</style>
